import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Account } from '@limo/database'
import { useAuth } from './AuthContext'

const IMPERSONATION_STORAGE_KEY = 'limo-builder-impersonation'

// Regex to match impersonation URL pattern: /admin/accounts/:accountSlug/view/*
const IMPERSONATION_URL_PATTERN = /^\/admin\/accounts\/([^/]+)\/view/

interface ImpersonationState {
  isImpersonating: boolean
  impersonatedAccountSlug: string | null
  originalAdminId: string | null
}

interface ImpersonationContextType extends ImpersonationState {
  impersonatedAccount: Account | null
  startImpersonation: (accountSlug: string) => Promise<void>
  stopImpersonation: () => void
}

const defaultState: ImpersonationState = {
  isImpersonating: false,
  impersonatedAccountSlug: null,
  originalAdminId: null,
}

const ImpersonationContext = createContext<
  ImpersonationContextType | undefined
>(undefined)

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { user, isAdmin, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [state, setState] = useState<ImpersonationState>(defaultState)
  const [impersonatedAccount, setImpersonatedAccount] =
    useState<Account | null>(null)

  // Load persisted state on mount
  useEffect(() => {
    const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ImpersonationState
        setState(parsed)
      } catch {
        localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
      }
    }
  }, [])

  // Auto-detect impersonation from URL pattern
  useEffect(() => {
    if (!isAdmin || !user?.id) return

    const match = location.pathname.match(IMPERSONATION_URL_PATTERN)
    if (match) {
      const urlAccountSlug = match[1]
      // Only update if we're not already impersonating this account
      if (!state.isImpersonating || state.impersonatedAccountSlug !== urlAccountSlug) {
        const newState: ImpersonationState = {
          isImpersonating: true,
          impersonatedAccountSlug: urlAccountSlug,
          originalAdminId: user.id,
        }
        setState(newState)
        localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(newState))
      }
    }
  }, [location.pathname, isAdmin, user?.id, state.isImpersonating, state.impersonatedAccountSlug])

  // Fetch impersonated account details when impersonating
  useEffect(() => {
    const fetchAccount = async () => {
      if (state.isImpersonating && state.impersonatedAccountSlug) {
        const { data, error } = await supabase
          .from('accounts')
          .select('*')
          .eq('slug', state.impersonatedAccountSlug)
          .single()

        if (error || !data) {
          console.error('Error fetching impersonated account:', error)
          // Account not found or error - stop impersonation (inline to avoid accessing function before declaration)
          setState(defaultState)
          setImpersonatedAccount(null)
          localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
          return
        }

        setImpersonatedAccount(data)
      } else {
        setImpersonatedAccount(null)
      }
    }

    fetchAccount()
  }, [state.isImpersonating, state.impersonatedAccountSlug])

  // Clear impersonation on session expiry
  useEffect(() => {
    if (!isAuthenticated && state.isImpersonating) {
      setState(defaultState)
      setImpersonatedAccount(null)
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
    }
  }, [isAuthenticated, state.isImpersonating])

  // Clear impersonation if user is not admin
  useEffect(() => {
    if (isAuthenticated && !isAdmin && state.isImpersonating) {
      setState(defaultState)
      setImpersonatedAccount(null)
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
    }
  }, [isAuthenticated, isAdmin, state.isImpersonating])

  const startImpersonation = useCallback(
    async (accountSlug: string) => {
      if (!isAdmin || !user?.id) {
        console.error('Only admins can impersonate accounts')
        return
      }

      const newState: ImpersonationState = {
        isImpersonating: true,
        impersonatedAccountSlug: accountSlug,
        originalAdminId: user.id,
      }

      setState(newState)
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(newState))
      navigate(`/admin/accounts/${accountSlug}/view/dashboard`)
    },
    [isAdmin, user?.id, navigate]
  )

  const stopImpersonation = useCallback(() => {
    setState(defaultState)
    setImpersonatedAccount(null)
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
    navigate('/admin/accounts')
  }, [navigate])

  const value: ImpersonationContextType = {
    ...state,
    impersonatedAccount,
    startImpersonation,
    stopImpersonation,
  }

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  )
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext)
  if (context === undefined) {
    throw new Error(
      'useImpersonation must be used within an ImpersonationProvider'
    )
  }
  return context
}
